import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConfig } from '@/core/config/ConfigProvider';

/**
 * META PIXEL (Facebook / Instagram)
 *
 * O trecho que o Meta manda colar no `<head>` faz duas coisas: carrega o
 * `fbevents.js` e dispara um `PageView`. Colado como veio, ele conta UMA
 * visita — a da primeira abertura — e nunca mais.
 *
 * Isto aqui é uma SPA: trocar de página não recarrega o documento, então o
 * `PageView` do trecho original nunca volta a rodar. O pixel reportaria uma
 * fração das visitas, e a campanha seria avaliada por um número errado para
 * menos. É por isso que o disparo mora num efeito de rota, e não no HTML.
 *
 * O `PageView` da primeira abertura também sai daqui, e não do carregador: com
 * os dois, toda primeira página seria contada em dobro.
 */

/**
 * O pixel do VENDOLOTE.
 *
 * Fica no código como PADRÃO, e não como única fonte: `integrations.metaPixelId`
 * na configuração do site tem precedência, e é o caminho para trocar de conta
 * ou DESLIGAR o rastreamento sem um deploy — basta publicar a chave vazia.
 *
 * Não é segredo. Um id de pixel é público por construção: ele aparece no
 * código-fonte de qualquer página que o use, e é assim que o Meta o lê.
 */
const PIXEL_PADRAO = '1654094442842771';

/**
 * Áreas internas não são audiência.
 *
 * Quem usa o painel é a equipe, e o painel do anunciante é área logada de
 * cliente. Mandar essas navegações para o Meta sujaria o público de
 * remarketing com o próprio time e entregaria os caminhos internos do sistema
 * a um terceiro — sem ninguém ter decidido isso.
 *
 * Como o carregamento é preguiçoso, o `fbevents.js` nem chega a ser baixado
 * para quem só entra no painel.
 */
const AREAS_INTERNAS = ['/admin', '/painel'];

type Fbq = ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Instala o `fbevents.js` uma vez. Repetir a chamada não faz nada. */
function carregar(pixelId: string): void {
  if (window.fbq) return;

  const fbq: Fbq = function (...args: unknown[]) {
    // `callMethod` existe depois que o script do Meta assume a fila.
    const alvo = fbq as unknown as { callMethod?: (...args: unknown[]) => void };

    if (alvo.callMethod) {
      alvo.callMethod(...args);
      return;
    }

    fbq.queue?.push(args);
  } as Fbq;

  window.fbq = fbq;
  window._fbq ??= fbq;

  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';

  const primeiro = document.getElementsByTagName('script')[0];
  primeiro?.parentNode?.insertBefore(script, primeiro);

  fbq('init', pixelId);
}

export function MetaPixel(): null {
  const { site } = useConfig();
  const { pathname, search } = useLocation();

  /*
    A CHAVE PRESENTE VENCE, mesmo vazia.

    `?? PIXEL_PADRAO` não serviria: ele só cobre a ausência, e publicar a chave
    vazia — que é como se desliga o rastreamento pelo painel — voltaria a cair
    no padrão. Presente é decisão de quem configurou; ausente é "ninguém
    decidiu ainda".
  */
  const configurado = site.integrations?.metaPixelId;
  const pixelId = (configurado === undefined ? PIXEL_PADRAO : configurado).trim();

  useEffect(() => {
    if (!pixelId) return;
    if (AREAS_INTERNAS.some((area) => pathname === area || pathname.startsWith(`${area}/`))) return;

    carregar(pixelId);
    window.fbq?.('track', 'PageView');

    /*
      `search` entra nas dependências porque a busca É a navegação aqui: ir de
      `/lotes?cidade=itauna` para `/lotes?cidade=betim` não muda o `pathname`,
      e são duas páginas diferentes para quem analisa a campanha.
    */
  }, [pixelId, pathname, search]);

  return null;
}
